import type { ReadonlySignal } from '@preact/signals-react';
import type { PoolChangeLogModel } from 'src/admin/models/PoolChangeLogModel';
import type { PoolInventoryModel } from 'src/admin/models/PoolInventoryModel';
import type { Pool, PoolPermission } from 'src/admin/models/PoolListModel';
import type { PoolPricingModel } from 'src/admin/models/PoolPricingModel';

/**
 * Tabs in the pool detail view. `info` is the default landing tab
 * (PLAT-12937 design): general pool info plus the pricing configuration
 * and simulator cards. `inventory` is the inventory table (renamed from
 * `lab-pool` in PLAT-12937). The `revenue` value is reserved here so the
 * type compiles when that tab lands in a future ticket.
 */
export type PoolDetailTab = 'info' | 'permissions' | 'inventory' | 'change-log' | 'revenue';

/**
 * The four-rung permission ladder, in UI vocabulary. Matches the
 * locates-ws `PermissionType` exactly so the wire mapping is a
 * pass-through.
 */
export type PermissionRole = 'reader' | 'writer' | 'owner' | 'admin';

/**
 * One row in the Permissions tab. The tab is scoped to firm members
 * only: even though the backend can store permissions for groups,
 * firms, or special pseudo-subjects, the UI explicitly restricts grants
 * (and the holder list) to individual users that belong to the pool's
 * firm. `userName` / `email` come from the firm-members directory; any
 * holder that doesn't resolve to a firm member is dropped from the view.
 */
export interface PoolPermissionHolder {
    /** UUID of the user — never shown directly. */
    userId: string;
    userName: string;
    email: string;
    /** The highest rung this user currently holds on the pool. */
    role: PermissionRole;
}

/** A firm-member candidate the grant form can offer. */
export interface PoolPermissionCandidate {
    userId: string;
    userName: string;
    email: string;
}

/**
 * The pool's editable info fields (PLAT-12970). `description` uses `''`
 * for "unset" so the textarea can bind directly; `eventEmail` is
 * required by the backend, so for it `''` is just the empty-input state
 * and fails the pool model's `isPoolInfoDraftValid`.
 */
export interface PoolInfoDraft {
    name: string;
    description: string;
    eventEmail: string;
}

/**
 * Mirrors the backend's notification-email validation for
 * `PATCH /v1/inventory/pools/{poolId}`, so a 400 is never the first
 * feedback. The pool model's `poolInfoDraftIssues` is the single
 * consumer; components read the projection.
 */
export const EVENT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Per-field validity of the pool model's `poolInfoDraft`. The Info tab
 * renders these as field-level errors, and `isPoolInfoDraftValid` folds
 * them into the submit gate, so both always agree.
 */
export interface PoolInfoDraftIssues {
    /** The required name is blank after trimming. */
    nameBlank: boolean;
    /** The required notification email fails {@link EVENT_EMAIL_PATTERN}. */
    emailMalformed: boolean;
}

/**
 * One open pool: identity, access gates, the detail tabs, the Info
 * tab's editable draft, the Permissions tab, and ownership of the
 * pool's three domain children (inventory, change log, pricing) plus
 * the unsaved-work aggregate over them. Pool identity — one instance
 * exists per open pool detail and is disposed on pool switch or
 * navigation, so drafts, dialogs, and staged work structurally cannot
 * leak between pools.
 */
export interface PoolModel extends Disposable {
    /** The pool this model represents; fixed for the instance's lifetime. */
    readonly poolId: string;
    /** The pool's payload resolved from the list; `null` until the list loads. */
    readonly pool: ReadonlySignal<Pool | null>;
    /** The caller's effective permission on the pool; `null` until loaded. */
    readonly permission: ReadonlySignal<PoolPermission | null>;
    /** Whether the pool is a legacy (SQL-backed) pool per the feature flag. */
    readonly isLegacy: ReadonlySignal<boolean>;

    // --- Access gates ---
    readonly canRead: ReadonlySignal<boolean>;
    readonly canWrite: ReadonlySignal<boolean>;
    readonly canOwn: ReadonlySignal<boolean>;
    /** Session-level superuser access; gates the Permissions tab and its mutations. */
    readonly canManagePermissions: ReadonlySignal<boolean>;

    // --- Detail tabs ---
    /**
     * Currently-selected tab. Defaults to `info` on a fresh instance
     * (every pool open lands on the Info tab). When
     * {@link canManagePermissions} is `false`, a stored `permissions`
     * value coerces to `info` on read so non-superusers never land on
     * the hidden tab.
     */
    readonly activePoolDetailTab: ReadonlySignal<PoolDetailTab>;
    setActivePoolDetailTab(tab: PoolDetailTab): void;

    // --- Children (constructed with the instance, fixed for its lifetime) ---
    readonly inventoryModel: PoolInventoryModel;
    readonly changeLogModel: PoolChangeLogModel;
    readonly pricingModel: PoolPricingModel;

    // --- Unsaved-work aggregates ---
    /**
     * `true` when anything on this pool would be lost by navigating
     * away: staged inventory work, an unsaved pricing config draft, or
     * an unsaved info draft. Feeds the owner's discard prompts and the
     * route blocker.
     */
    readonly hasUnsavedChanges: ReadonlySignal<boolean>;
    /**
     * `true` while the submit-changes bar has something to show: the
     * caller can write and the inventory has staged changes, an
     * in-flight submit, or a submit error.
     */
    readonly showSubmitChangesBar: ReadonlySignal<boolean>;

    // --- Pool info editing (PLAT-12970) ---
    /** `true` when the caller may edit name / description / email (owner). */
    readonly canEditPoolInfo: ReadonlySignal<boolean>;
    /** The pool's info fields as last seen from the server. */
    readonly poolInfoSaved: ReadonlySignal<PoolInfoDraft>;
    /** The Info tab's editable draft; reseats from the server under a clean draft. */
    readonly poolInfoDraft: ReadonlySignal<PoolInfoDraft>;
    readonly isPoolInfoDirty: ReadonlySignal<boolean>;
    /** Per-field validity of the info draft. */
    readonly poolInfoDraftIssues: ReadonlySignal<PoolInfoDraftIssues>;
    /** `false` while any issue in {@link poolInfoDraftIssues} is flagged. */
    readonly isPoolInfoDraftValid: ReadonlySignal<boolean>;
    /** `true` while the info PATCH is in flight; the submit button disables. */
    readonly isSavingPoolInfo: ReadonlySignal<boolean>;
    setPoolInfoField(field: keyof PoolInfoDraft, value: string): void;
    discardPoolInfoChanges(): void;
    /**
     * PATCH the changed info fields onto the pool, toast the outcome,
     * and reconcile the baseline against the server's echo.
     */
    submitPoolInfo(): void;

    // --- Pool permissions (Permissions tab) ---
    /**
     * Firm-member permission holders for this pool, each at their
     * highest rung. Group / firm / pseudo-subject grants are not
     * surfaced — the tab is deliberately firm-members-only.
     */
    readonly poolPermissionHolders: ReadonlySignal<ReadonlyArray<PoolPermissionHolder>>;
    /** Firm members without a direct grant; drives the "Grant access" selector. */
    readonly poolPermissionCandidates: ReadonlySignal<ReadonlyArray<PoolPermissionCandidate>>;
    /** Staged role change for the confirm dialog, or `null`. */
    readonly pendingPermissionChange: ReadonlySignal<{
        userId: string;
        userName: string;
        role: PermissionRole;
    } | null>;
    /** True while any pool-permissions PUT call is in flight. */
    readonly permissionsMutating: ReadonlySignal<boolean>;
    /** Open the confirm dialog to flip an existing holder's role. */
    requestSetPoolPermissionRole(userId: string, role: PermissionRole): void;
    cancelSetPoolPermissionRole(): void;
    confirmSetPoolPermissionRole(): Promise<void>;
    /** Grant `role` to the firm-member `userId` directly (no confirm prompt). */
    addPoolPermission(userId: string, role: PermissionRole): Promise<void>;
    /** Revoke every permission rung this user holds on the pool. */
    removePoolPermission(userId: string): Promise<void>;
}
